import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createSupportTicket,
  CreateTicketInput,
  DEFAULT_UPLOAD_LIMITS,
  getSupportLimits,
  getSupportTicket,
  isUnreadForUser,
  listSupportTickets,
  markSupportTicketRead,
  sendSupportMessage,
  SupportMessage,
  SupportTicketDetail,
  SupportTicketSummary,
  SupportUploadLimits,
  UploadProgress,
} from '../../services/supportApi';
import supportWebSocket from '../../services/supportWebSocket';

export type SupportView = 'list' | 'new' | 'thread';

/**
 * All widget state in one place: the ticket list (which also feeds the badge), the open thread,
 * and the socket fold. Socket events only ever patch what is already loaded; anything the fold
 * cannot express falls back to a refetch when the panel opens.
 */
export function useSupport(enabled: boolean) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<SupportView>('list');
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [active, setActive] = useState<SupportTicketDetail | null>(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [limits, setLimits] = useState<SupportUploadLimits>(DEFAULT_UPLOAD_LIMITS);

  // Refs mirror the bits the socket listeners need without re-subscribing on every render.
  const activeIdRef = useRef<string | null>(null);
  const viewingRef = useRef(false);
  activeIdRef.current = active?.id ?? null;
  viewingRef.current = open && view === 'thread';

  const unread = useMemo(() => tickets.filter(isUnreadForUser).length, [tickets]);

  const refreshTickets = useCallback(async () => {
    setListLoading(true);
    try {
      const rows = await listSupportTickets();
      setTickets(rows);
      setListError(null);
    } catch (e: any) {
      setListError(e?.response?.data?.message || 'Could not load your tickets.');
    } finally {
      setListLoading(false);
    }
  }, []);

  const upsertTicket = useCallback((t: SupportTicketSummary) => {
    setTickets((prev) => {
      const rest = prev.filter((x) => x.id !== t.id);
      return [t, ...rest].sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
      );
    });
  }, []);

  const markRead = useCallback(
    async (ticketId: string) => {
      try {
        const summary = await markSupportTicketRead(ticketId);
        upsertTicket(summary);
        setActive((cur) => (cur && cur.id === ticketId ? { ...cur, ...summary } : cur));
      } catch {
        // Read receipts are best-effort; the next open retries.
      }
    },
    [upsertTicket],
  );

  // Boot: list + limits + socket. Torn down when the user signs out.
  useEffect(() => {
    if (!enabled) {
      supportWebSocket.disconnect();
      setTickets([]);
      setActive(null);
      setOpen(false);
      return;
    }
    refreshTickets();
    getSupportLimits().then(setLimits).catch(() => undefined);
    supportWebSocket.connect();

    const offTicket = supportWebSocket.onTicket((t) => {
      upsertTicket(t);
      setActive((cur) => (cur && cur.id === t.id ? { ...cur, ...t, messages: cur.messages } : cur));
    });
    const offMessage = supportWebSocket.onMessage((m: SupportMessage) => {
      if (m.ticketId !== activeIdRef.current) return;
      setActive((cur) => {
        if (!cur || cur.id !== m.ticketId) return cur;
        if (cur.messages.some((x) => x.id === m.id)) return cur;
        return { ...cur, messages: [...cur.messages, m] };
      });
      if (viewingRef.current && m.senderRole === 'ADMIN') markRead(m.ticketId);
    });
    return () => {
      offTicket();
      offMessage();
    };
  }, [enabled, refreshTickets, upsertTicket, markRead]);

  const openPanel = useCallback(() => {
    setOpen(true);
    refreshTickets();
  }, [refreshTickets]);

  const closePanel = useCallback(() => setOpen(false), []);

  const showList = useCallback(() => {
    setView('list');
    setActive(null);
  }, []);

  const showNew = useCallback(() => setView('new'), []);

  const openTicket = useCallback(
    async (ticketId: string) => {
      setView('thread');
      setActiveLoading(true);
      try {
        const detail = await getSupportTicket(ticketId);
        setActive(detail);
        if (detail.unread) markRead(ticketId);
      } finally {
        setActiveLoading(false);
      }
    },
    [markRead],
  );

  const createTicket = useCallback(
    async (input: CreateTicketInput, onProgress?: UploadProgress) => {
      const detail = await createSupportTicket(input, onProgress);
      upsertTicket(detail);
      setActive(detail);
      setView('thread');
      return detail;
    },
    [upsertTicket],
  );

  const sendMessage = useCallback(
    async (body: string, files: File[], onProgress?: UploadProgress) => {
      if (!active) return null;
      const m = await sendSupportMessage(active.id, body, files, onProgress);
      setActive((cur) =>
        cur && cur.id === m.ticketId && !cur.messages.some((x) => x.id === m.id)
          ? { ...cur, messages: [...cur.messages, m] }
          : cur,
      );
      return m;
    },
    [active],
  );

  return {
    open,
    view,
    tickets,
    unread,
    listLoading,
    listError,
    active,
    activeLoading,
    limits,
    openPanel,
    closePanel,
    showList,
    showNew,
    openTicket,
    createTicket,
    sendMessage,
    refreshTickets,
  };
}

export type SupportController = ReturnType<typeof useSupport>;
